import { useMutation, useQueryClient } from "@tanstack/react-query";

import * as flowApi from "../api";
import { flowQueryKeys } from "../query-options";
import type {
  FlowRule,
  FlowRuleDraft,
  ListFlowRulesReply,
  ReorderFlowRuleVariables,
  UpdateFlowRuleRequest,
} from "../types";

export function useCreateFlowRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: FlowRuleDraft) => flowApi.createFlowRule(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: flowQueryKeys.rules });
    },
  });
}

export function useUpdateFlowRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFlowRuleRequest }) =>
      flowApi.updateFlowRule(id, data),
    onSuccess: (rule) => {
      queryClient.setQueryData(flowQueryKeys.rule(rule.id), rule);
      void queryClient.invalidateQueries({ queryKey: flowQueryKeys.rules });
    },
  });
}

export function useToggleFlowRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      flowApi.updateFlowRule(id, { enabled }),
    onMutate: async ({ id, enabled }) => {
      await queryClient.cancelQueries({ queryKey: flowQueryKeys.rules });
      const previous = queryClient.getQueryData<ListFlowRulesReply>(flowQueryKeys.rules);
      queryClient.setQueryData<ListFlowRulesReply>(flowQueryKeys.rules, (current) => ({
        rules: (current?.rules ?? []).map((rule) =>
          rule.id === id ? { ...rule, enabled } : rule,
        ),
      }));
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(flowQueryKeys.rules, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: flowQueryKeys.rules });
    },
  });
}

export function useReorderFlowRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, position }: ReorderFlowRuleVariables) =>
      flowApi.updateFlowRule(id, { position }),
    onMutate: async ({ nextRules }) => {
      await queryClient.cancelQueries({ queryKey: flowQueryKeys.rules });
      const previous = queryClient.getQueryData<ListFlowRulesReply>(flowQueryKeys.rules);
      queryClient.setQueryData<ListFlowRulesReply>(flowQueryKeys.rules, {
        rules: nextRules,
      });
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(flowQueryKeys.rules, context.previous);
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: flowQueryKeys.rules });
    },
  });
}

export function useDeleteFlowRuleMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => flowApi.deleteFlowRule(id),
    onSuccess: (_reply, id) => {
      queryClient.removeQueries({ queryKey: flowQueryKeys.rule(id) });
      void queryClient.invalidateQueries({ queryKey: flowQueryKeys.rules });
    },
  });
}

export function sortFlowRules(rules: FlowRule[]) {
  return [...rules].sort((left, right) => left.sort_order - right.sort_order);
}

